// src/components/BarcodeScannerWrapper.jsx
import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import BarcodeScannerCore from './BarcodeScannerCore';
// ⚙️ foundational engine:
import { BarcodeReader, EnumRegionPredetectionMode } from 'dynamsoft-javascript-barcode';

const PREFERRED_CAMERA_KEY = 'preferredCameraId';
const region = {
  regionLeft: 25,
  regionTop: 40,
  regionRight: 75, // 25 + 50 width
  regionBottom: 60, // 40 + 20 height
  regionMeasuredByPercentage: 1
};

const chooseBestCamera = (devices) => {
  const scored = devices.map((device) => {
    const label = device.label.toLowerCase();
    let score = 0;

    if (label.includes('ultra') && label.includes('wide') && label.includes('back')) {
      score += 100; // prioritize "Back Ultra Wide"
    }

    if (label.includes('back') || label.includes('rear')) score += 5;
    if (label.includes('environment')) score += 4;
    if (!label.includes('wide') && !label.includes('ultra')) score += 3;
    if (label.includes('0')) score += 1;

    return { device, score };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored.length > 0 ? scored[0].device : devices[0];
};

export default function BarcodeScannerWrapper({ show, onBarcodeDetected, onClose, beepRef }) {
  const [reader, setReader] = useState(null);
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(null);
  const videoRef = useRef(null);

  // 1️⃣ Create engine & list cameras
  useEffect(() => {
    if (!show) return;
    let mounted = true;

    (async () => {
      // Instantiate the core engine
      const r = await BarcodeReader.createInstance();
      if (!mounted) return;
      setReader(r);

      // Apply ROI (percent integers) + predetection
      const settings = await r.getRuntimeSettings();
      settings.region.regionLeft = 5;
      settings.region.regionTop = 40;
      settings.region.regionRight = 95;
      settings.region.regionBottom = 60;
      settings.region.regionMeasuredByPercentage = 1;
      settings.region.regionPredetectionMode = EnumRegionPredetectionMode.RPM_GENERAL;


      await r.updateRuntimeSettings(settings);
      //console.log('📋 Applied settings:', await r.getRuntimeSettings());


      // Enumerate cameras
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cams = devices.filter(d => d.kind === 'videoinput');
      if (!mounted) return;
      const back = cams.filter(c => !/front|user/i.test(c.label));
      const list = back.length ? back : cams;
      setDevices(list);

      // Pick preferred or best
      const saved = localStorage.getItem(PREFERRED_CAMERA_KEY);
      const found = list.find(d => d.deviceId === saved);
      const best = found || chooseBestCamera(list) || list[0];
      setDeviceId(best.deviceId);
      localStorage.setItem(PREFERRED_CAMERA_KEY, best.deviceId);
    })();

    return () => { mounted = false; };
  }, [show]);

  // 2️⃣ Start/stop scanning by decoding frames
  useEffect(() => {
    if (!reader || !deviceId) return;
    let active = true;
    let stream;

    (async () => {
      // Hook camera into our <video>
      stream = await navigator.mediaDevices.getUserMedia({ video: { deviceId } });
      videoRef.current.srcObject = stream;
      await videoRef.current.play();

      // Loop calling decode()
      const scanLoop = async () => {
        if (!active) return;
        try {
          const results = await reader.decode(videoRef.current);
          if (results.length) {
            active = false;
            const text = results[0].barcodeText;
            beepRef.current?.play().catch(() => { });
            onBarcodeDetected(text);
            onClose();
          } else {
            requestAnimationFrame(scanLoop);
          }
        } catch (e) {
          console.error('decode error', e);
          requestAnimationFrame(scanLoop);
        }
      };
      scanLoop();
    })();

    return () => {
      active = false;
      stream?.getTracks().forEach(t => t.stop());
    };
  }, [reader, deviceId]);

  const handleSelect = e => {
    const id = e.target.value;
    setDeviceId(id);
    localStorage.setItem(PREFERRED_CAMERA_KEY, id);
  };

  if (!show || !deviceId) return null;

  return ReactDOM.createPortal(
    <BarcodeScannerCore
      videoRef={videoRef}
      videoDevices={devices}
      selectedDeviceId={deviceId}
      onSelectDevice={handleSelect}
      onClose={onClose}
      beepRef={beepRef}
    />,
    document.getElementById('scanner-portal-root')
  );
}
